// A BYOK key CAPTURE's answer (plan 102 slice 05, decision 4, option B), shared by
// the org page (`POST /v1/billing/keys`) and My keys (`POST /v1/me/keys`).
//
// Both doors run the Verify click's own probe right after the capture commits and
// answer the masked row (its status AFTER that probe) plus
// `verification: { outcome, reason, fault }` (`apps/api/src/routes/key-capture-answer.ts`):
//
//   verified          the key passed.
//   model_parked      the credential is good, the model's leg failed (`fault` set).
//   transport_failed  the provider was unreachable; the key stays `unverified`.
//   invalid           the provider refused it; the row is now `invalid`.
//   not_run           the probe never ran; the key is stored as captured.
//
// The capture is ALWAYS a 201: the key exists whatever the probe said, so none of
// these is a failed add. Only `verified` is a pass. `not_run`'s `reason` names an
// API route, which is the MCP/curl caller's next step, not a person's: the page
// says the same thing in its own words and offers the Verify action instead.
//
// The body is parsed: a 201 without a known `verification` THROWS (the add's error
// line, then the refetch shows the stored row), so a probe nobody ran can never read
// as a verified key.

import { z } from 'zod';
import { t } from '../i18n';
import { keyVerifyNotice } from './key-verify';
import type { KeyRow } from './keys-types';
import type { ToastController, ToastTone } from './toast';

const KeyCaptureVerificationSchema = z.object({
  outcome: z.enum(['verified', 'model_parked', 'transport_failed', 'invalid', 'not_run']),
  reason: z.string().nullable(),
  // A string, like the verify answer's: an unknown fault kind must not make a 201
  // unparseable (`lib/key-verify.ts` `probeFaultRoute` routes it).
  fault: z.string().nullable(),
});

export type KeyCaptureVerification = z.infer<typeof KeyCaptureVerificationSchema>;

/** The 201, split: the stored row and what the inline probe said about it. */
export type KeyCaptureAnswer = {
  readonly key: KeyRow;
  readonly verification: KeyCaptureVerification;
};

/** The capture body's row and verification, or a throw naming what came back instead. */
export function parseKeyCapture(body: unknown): KeyCaptureAnswer {
  const parsed = z
    .object({ verification: KeyCaptureVerificationSchema })
    .passthrough()
    .safeParse(body);
  if (!parsed.success) {
    throw new Error(
      `key capture answered 201 without a known verification { outcome, reason, fault }: ${parsed.error.message}`,
    );
  }
  const { verification, ...key } = parsed.data;
  return { key: key as unknown as KeyRow, verification };
}

/** The notice one capture earns; `offerVerify` asks the page to put Verify on it. */
export function keyCaptureNotice(v: KeyCaptureVerification): {
  readonly message: string;
  readonly tone: ToastTone;
  readonly offerVerify: boolean;
} {
  const reason = v.reason ?? '';
  switch (v.outcome) {
    case 'verified':
      return {
        message: t('account_keys.added_verified_toast'),
        tone: 'success',
        offerVerify: false,
      };
    case 'invalid':
      return {
        message: t('account_keys.added_invalid_toast', { reason }),
        tone: 'error',
        offerVerify: false,
      };
    case 'transport_failed':
      return {
        message: t('account_keys.added_unreachable_toast', { reason }),
        tone: 'warning',
        offerVerify: true,
      };
    case 'model_parked': {
      const parked = keyVerifyNotice({ outcome: 'model_parked', reason: v.reason, fault: v.fault });
      return {
        message: t('account_keys.added_then', { notice: parked.message }),
        tone: 'warning',
        offerVerify: false,
      };
    }
    case 'not_run':
      return { message: t('account_keys.added_not_run_toast'), tone: 'warning', offerVerify: true };
  }
}

/**
 * Toast what one capture found, with Verify on the notice when checking again is
 * the next step (`verify` is the page's own Verify action, run on the stored row).
 */
export function notifyKeyCapture(
  answer: KeyCaptureAnswer,
  notify: ToastController['notify'],
  verify: (key: KeyRow) => unknown,
): void {
  const notice = keyCaptureNotice(answer.verification);
  const action = notice.offerVerify
    ? { label: t('account_keys.verify'), run: () => void verify(answer.key) }
    : undefined;
  notify(notice.message, notice.tone, action);
}
